import { Form } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useLazyGetClientByIdQuery } from '@app/api/Client/api';
import {
  useCreateMedicalDocumentMutation,
  useSendToMedFlexMutation,
  useSubmitMedicalDocumentMutation,
  useUpdateMedicalCardMutation,
  useUpdateMedicalDocumentStatusMutation,
} from '@app/api/MedicalDocuments';
import type {
  ModelMedicalDocumentCreate,
  ModelMedicalDocumentResponse,
  SignerSignRole,
} from '@app/api/MedicalDocuments';
import {
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
} from '@app/api/User/api';
import { selectRecordModal } from '@app/components/RecordModal/model/selectors';
import { SUCCESS } from '@app/constants';
import { useMedicalDocumentsAccessRights, useUserPrint } from '@app/hooks';
import { DocumentStatus } from '@app/pages/MedicalCard/MedicalCardRegistry/types';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { useAppSelector } from '@app/store';

import { CDA_4_KIND } from '../data';

import type { MedicalCardFormValues } from '../types';

export const useMedicalCard = () => {
  const [initialData, setInitialData] = useState<
    ModelMedicalDocumentResponse[]
  >([]);
  // Роль, для которой открыта модалка подписания; null — модалка закрыта.
  const [signingRole, setSigningRole] = useState<SignerSignRole | null>(null);

  const { activeBranchId: ActiveBranchId, userId } = useUserPrint();
  const {
    hasUserRightToEditMedicalDocuments,
    hasUserRightToSignMedicalDocuments,
    hasUserRightToSendMedicalDocuments,
  } = useMedicalDocumentsAccessRights();

  const { showNotification } = useContext(NotificationContext);

  const { clientId, record } = useAppSelector(selectRecordModal);

  const [form] = Form.useForm();

  const [createMedicalDocument, { isLoading: isCreateLoading }] =
    useCreateMedicalDocumentMutation();
  const [updateMedicalCard, { isLoading: isUpdateLoading }] =
    useUpdateMedicalCardMutation();
  const [sendToMedFlex, { isLoading: isSendToMedFlexLoading }] =
    useSendToMedFlexMutation();
  const [updateDocumentStatus, { isLoading: isUpdateStatusLoading }] =
    useUpdateMedicalDocumentStatusMutation();
  const [getClientById, { isFetching: isGetClientByIdLoading }] =
    useLazyGetClientByIdQuery();
  const [getUserById, { isFetching: isGetUserByIdLoading }] =
    useLazyGetUserByIdQuery();
  const [submitMedicalDocument, { isLoading: isSubmitLoading }] =
    useSubmitMedicalDocumentMutation();

  // Роль подписанта текущего пользователя (SignerRole из карточки
  // сотрудника) — определяет, какие кнопки подписания ему доступны.
  const { data: currentUser } = useGetUserByIdQuery(
    { getUserId: userId as number, ActiveBranchId },
    { skip: !userId || !ActiveBranchId }
  );

  const isLoading =
    isCreateLoading ||
    isUpdateLoading ||
    isSendToMedFlexLoading ||
    isUpdateStatusLoading ||
    isSubmitLoading;

  const isGetPresetDataLoading = isGetClientByIdLoading || isGetUserByIdLoading;

  const medicalDocument = initialData?.[0];

  const isFormDisabled =
    (!!medicalDocument && medicalDocument.Status !== DocumentStatus.DRAFT) ||
    !hasUserRightToEditMedicalDocuments;
  /// Подписан. Документ подписан врачом и руководителем, готов к отправке.
  const isSignedStatus =
    !!medicalDocument && medicalDocument.Status === DocumentStatus.SIGNED;

  const isDraftStatus =
    !!medicalDocument && medicalDocument.Status === DocumentStatus.DRAFT;
  const isPendingSignStatus =
    !!medicalDocument && medicalDocument.Status === DocumentStatus.PENDING_SIGN;
  // Регистрация в РЭМД не прошла — отправку можно повторить.
  const isErrorStatus =
    !!medicalDocument && medicalDocument.Status === DocumentStatus.ERROR;

  const isDoctorSigned = !!medicalDocument?.DoctorSignedByUserId;
  const isChiefSigned = !!medicalDocument?.ChiefSignedByUserId;

  const currentSignerRole = currentUser?.SignerRole ?? null;

  const canSubmitForSigning =
    isDraftStatus && hasUserRightToEditMedicalDocuments;

  // Порядок подписей строгий: сначала врач, затем организация. Кнопки
  // гейтятся правом IsSign (task/10230) и ролью подписанта из карточки
  // сотрудника (SignerRole).
  const canSignAsDoctor =
    isPendingSignStatus &&
    !isDoctorSigned &&
    hasUserRightToSignMedicalDocuments &&
    currentSignerRole === 'DOCTOR';
  const canSignAsChief =
    isPendingSignStatus &&
    isDoctorSigned &&
    !isChiefSigned &&
    hasUserRightToSignMedicalDocuments &&
    currentSignerRole === 'CHIEF';

  const canSendToMedFlex =
    (isSignedStatus || isErrorStatus) && hasUserRightToSendMedicalDocuments;
  // На ERROR это повторная отправка — метка кнопки отличается.
  const isResendToMedFlex = isErrorStatus;

  const mapPersonData = (person: Record<string, unknown>) => {
    const document = person.document as Record<string, unknown> | undefined;

    return {
      LocalId: person.localId,
      Surname: person.surname,
      Name: person.name,
      Patronymic: person.patronymic,
      BirthDate: person.birthDate
        ? dayjs(person.birthDate as string)
        : undefined,
      Snils: person.snils,
      Gender: person.gender,
      Phone: person.phone,
      Address: person.address,

      Document:
        document && typeof document === 'object'
          ? {
              Type: document.type as string | undefined,
              Number: document.number as string | undefined,
            }
          : undefined,

      Role: person.role,
      Email: person.email,
      Position: person.position,
      PositionCode: person.positionCode as string | undefined,
      Speciality: person.speciality as string | undefined,
    };
  };

  const patientData = useMemo(
    () => mapPersonData(JSON.parse(initialData?.[0]?.Patient || '{}') || {}),
    [initialData]
  );
  const doctorData = useMemo(
    () => mapPersonData(JSON.parse(initialData?.[0]?.Doctor || '{}') || {}),
    [initialData]
  );

  useEffect(() => {
    if (!initialData?.length) return;

    const [medicalDocument] = initialData;

    form.setFieldsValue({
      DocumentNumber: medicalDocument.DocumentNumber,

      ClinicalData: medicalDocument.ClinicalData,

      Patient: patientData,
      Doctor: doctorData,
    });
  }, [doctorData, form, initialData, patientData]);

  const handleFinish = async (values: MedicalCardFormValues) => {
    try {
      if (!clientId) {
        showNotification('Ошибка: clientId не найден');

        return;
      }

      const normalizedPatient = {
        ...values.Patient,

        BirthDate: values.Patient?.BirthDate
          ? dayjs(values.Patient.BirthDate).format('YYYY-MM-DD')
          : '',
      };

      const normalizedDoctor = {
        ...values.Doctor,

        BirthDate: values.Doctor?.BirthDate
          ? dayjs(values.Doctor.BirthDate).format('YYYY-MM-DD')
          : '',
      };

      if (initialData?.[0]) {
        const payload = {
          ResponsibleDoctorId: record?.EmployeeId!!,

          ClinicalData: values.ClinicalData,

          Patient: normalizedPatient,

          Doctor: normalizedDoctor,
        };

        const result = await updateMedicalCard({
          ActiveBranchId,

          id: initialData[0].Id,

          data: payload,
        }).unwrap();

        setInitialData([result]);

        showNotification('Медицинская карта успешно обновлена', SUCCESS);

        return;
      }

      const payload: ModelMedicalDocumentCreate = {
        ...values,

        PatientId: clientId,
        RecordId: record?.Id!!,
        ResponsibleDoctorId: record?.EmployeeId!!,

        Kind: CDA_4_KIND,

        Patient: normalizedPatient,

        Doctor: normalizedDoctor,
      };

      const result = await createMedicalDocument({
        ActiveBranchId,
        data: payload,
      }).unwrap();

      setInitialData([result]);

      showNotification('Медицинская карта успешно сохранена', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  const handleSendToMedFlex = async () => {
    try {
      if (!initialData?.[0]) {
        showNotification('Ошибка: медицинская карта не найдена');

        return;
      }

      const id = initialData[0].Id;

      await sendToMedFlex({
        ActiveBranchId,
        id,
      }).unwrap();

      showNotification(
        'Медицинская карта успешно отправлена в MedFlex',
        SUCCESS
      );

      // Статус на сервере меняется после отправки — точечно перечитываем
      // документ, чтобы бейдж не «замирал» на SENT.
      const updated = await updateDocumentStatus({
        ActiveBranchId,
        id,
      }).unwrap();

      setInitialData([updated]);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  // DRAFT -> PENDING_SIGN: бэк валидирует, генерит XML СЭМД и открывает
  // документ для подписания.
  const handleSubmitForSigning = async () => {
    if (!medicalDocument || !ActiveBranchId) return;

    try {
      const updated = await submitMedicalDocument({
        ActiveBranchId,
        id: medicalDocument.Id,
      }).unwrap();

      setInitialData([updated]);

      showNotification('Документ отправлен на подпись', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  const openSigning = (role: SignerSignRole) => setSigningRole(role);

  const closeSigning = () => setSigningRole(null);

  const handleSigned = (updated: ModelMedicalDocumentResponse) =>
    setInitialData([updated]);

  const getClientInfo = useCallback(async () => {
    try {
      if (!clientId) {
        showNotification('Ошибка: clientId не найден');
        return;
      }

      const response = await getClientById({
        clientId,
        ActiveBranchId,
      }).unwrap();
      let gender: string | undefined;

      switch (response.Gender) {
        case 'm':
          gender = '1';
          break;
        case 'f':
          gender = '2';
          break;
        default:
          gender = undefined;
      }

      form.setFieldsValue({
        Patient: {
          Surname: response.NameLast,
          Name: response.NameFirst,
          Patronymic: response.NameMiddle,
          BirthDate: response.DateBirth ? dayjs(response.DateBirth) : undefined,
          Snils: response.Snils,
          Gender: gender,
          Phone: response.Phone1,
          Address: response.RegistrationAddress,
          Document: {
            Type: response.IdentityDocumentType,
            Number: response.IdentityDocumentNumber,
          },
        },
      });
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [ActiveBranchId, clientId, form, getClientById, showNotification]);

  const getDoctorInfo = useCallback(async () => {
    try {
      if (!record?.EmployeeId) {
        showNotification('Ошибка: EmployeeId не найден');
        return;
      }

      const response = await getUserById({
        getUserId: record?.EmployeeId,
        ActiveBranchId,
      }).unwrap();

      form.setFieldsValue({
        Doctor: {
          Surname: response.NameLast,
          Name: response.NameFirst,
          Patronymic: response.NameMiddle || undefined,
          BirthDate: response.DateBirth ? dayjs(response.DateBirth) : undefined,
          Snils: response.Snils || undefined,
          Role: response.SignerRole || undefined,
          PositionCode: response.PositionCode || undefined,
          Speciality: response.SpecialityCodeNsi || undefined,
        },
      });
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [ActiveBranchId, record?.EmployeeId, form, getUserById, showNotification]);

  return {
    form,
    handleFinish,
    isFormDisabled,
    isSignedStatus,
    isDraftStatus,
    isPendingSignStatus,
    isDoctorSigned,
    isChiefSigned,
    canSubmitForSigning,
    canSignAsDoctor,
    canSignAsChief,
    canSendToMedFlex,
    isResendToMedFlex,
    signingRole,
    openSigning,
    closeSigning,
    handleSigned,
    handleSubmitForSigning,
    medicalDocument,
    updateMedicalCard,
    handleSendToMedFlex,
    isLoading,
    getClientInfo,
    setInitialData,
    getDoctorInfo,
    isGetPresetDataLoading,
    patientData,
    initialData,
    doctorData,
  };
};
